import { Box, Container, Flex, Group, Switch, Text } from "@mantine/core";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { UseFormReturnType } from "@mantine/form";
import type { ReactNode } from "react";
import { EventFormValues } from "./types";
import dayjs from "dayjs";
import { useQueryOrganizer } from "hooks/organizers";

interface EventFormDateTimeProps {
  form: UseFormReturnType<EventFormValues>;
  t: (key: string) => string;
  isNew: boolean;
  weekly: boolean;
  setWeekly: (value: boolean) => void;
  currOrganizerId?: number;
  /**
   * Desk mode: wraps the start and the end picker separately. Absent
   * everywhere else, which renders both pickers exactly as before.
   */
  wrapField?: (field: "start" | "end", node: ReactNode) => ReactNode;
}

const noWrap = (_field: "start" | "end", node: ReactNode) => node;

// Wide enough for "DD/MM/YYYY HH:mm" plus the calendar icon. The pickers sit in
// a half-width column, which in the desk is a quarter of the screen: below
// this width the end picker wraps under the start one instead of both
// shrinking until the value is cut off.
const PICKER_MIN_WIDTH = 220;
const pickerCellStyle = {
  flex: `1 1 ${PICKER_MIN_WIDTH}px`,
  minWidth: PICKER_MIN_WIDTH,
};

export const EventFormDateTime = ({
  form,
  t,
  isNew,
  weekly,
  setWeekly,
  currOrganizerId,
  wrapField = noWrap,
}: EventFormDateTimeProps) => {
  // Fetch organizer details if currOrganizerId is provided
  const { organizer } = useQueryOrganizer({ id: currOrganizerId });
  const canEnableWeekly = organizer?.enable_weekly || false;

  // With no start date there is no month to name, so the switch reads as the
  // bare label rather than formatting a null through dayjs.
  const weeklyLabel = form.values.startTime
    ? `${t("events.weekly")} ${dayjs(form.values.startTime).format("MMMM")}`
    : t("events.weekly");

  return (
    <Group mt="md">
      <Container style={{ position: "relative", padding: 0 }}>
        <Text
          style={{
            position: "absolute",
            top: -10,
            left: 10,
            backgroundColor: "white",
            padding: "0 5px",
            color: "#6c757d",
          }}
          size="sm"
        >
          {t("events.time")}
        </Text>
        <Container
          style={{
            border: "1px #CED4DA solid",
            alignItems: "center",
          }}
          p="lg"
          pt="xl"
        >
          <Flex wrap="wrap" gap="md">
            <Box style={pickerCellStyle}>
              {wrapField(
                "start",
                <DateTimePicker
                  label={t("events.start_date")}
                  // A null date must render as an EMPTY control (FBIMP-12).
                  // dayjs(null) is invalid-but-truthy, so an unconditional wrap
                  // would show a garbage value instead of nothing.
                  value={form.values.startTime ? dayjs(form.values.startTime) : null}
                  ampm={false}
                  onChange={(value) => {
                    form.setFieldValue("startTime", value ? value.toDate() : null);

                    if (
                      value &&
                      form.values.finishTime &&
                      dayjs(value).isAfter(dayjs(form.values.finishTime), "date")
                    ) {
                      const date1 = dayjs(value);
                      const date2 = dayjs(form.values.finishTime);
                      form.setFieldValue(
                        "finishTime",
                        date2
                          .set("year", date1.year())
                          .set("month", date1.month())
                          .set("date", date1.date())
                          .toDate()
                      );
                    }
                  }}
                  minDate={dayjs()}
                  timezone="UTC"
                  format="DD/MM/YYYY HH:mm"
                  // The pre-conversion gate sets field errors on startTime and
                  // finishTime, and a block that names a field the curator cannot
                  // see is not a block, it is a dead button. MUI ignores a bare
                  // `error` prop here — the message has to go through slotProps.
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!form.errors.startTime,
                      helperText: form.errors.startTime,
                    },
                  }}
                />
              )}
            </Box>
            <Box style={pickerCellStyle}>
              {wrapField(
                "end",
                <DateTimePicker
                  label={t("events.end_date")}
                  {...form.getInputProps("finishTime")}
                  value={
                    form.values.finishTime ? dayjs(form.values.finishTime) : null
                  }
                  onChange={(value) =>
                    form.setFieldValue(
                      "finishTime",
                      value ? value.toDate() : null
                    )
                  }
                  ampm={false}
                  timezone="UTC"
                  format="DD/MM/YYYY HH:mm"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!form.errors.finishTime,
                      helperText: form.errors.finishTime,
                    },
                  }}
                />
              )}
            </Box>
          </Flex>
          {isNew && (canEnableWeekly || !currOrganizerId) && (
            <Flex mt="md">
              <Switch
                size="lg"
                onLabel={weeklyLabel}
                offLabel={weeklyLabel}
                color="blue"
                checked={weekly}
                onChange={() => setWeekly(!weekly)}
              />
            </Flex>
          )}
        </Container>
      </Container>
    </Group>
  );
};
